import type { OperationNodeSpec, OperationTypeDefinition } from '@/graph/domain/types';
import type { AddStepFormState } from './types';
import { INITIAL_FORM_STATE } from './types';
import type { RecommendedInstrumentRef } from './types';

function normalize(text: string | undefined): string {
  return String(text ?? '').trim().toLowerCase();
}

function pickLatest(entries: OperationTypeDefinition[]): OperationTypeDefinition | undefined {
  if (entries.length === 0) return undefined;
  return [...entries].sort((a, b) => {
    if (Boolean(a.isCurrentVersion) !== Boolean(b.isCurrentVersion)) {
      return a.isCurrentVersion ? -1 : 1;
    }
    if ((a.versionNumber ?? 0) !== (b.versionNumber ?? 0)) {
      return (b.versionNumber ?? 0) - (a.versionNumber ?? 0);
    }
    const aTime = Date.parse(a.updatedOn || a.createdOn || '') || 0;
    const bTime = Date.parse(b.updatedOn || b.createdOn || '') || 0;
    return bTime - aTime;
  })[0];
}

function toRecommendedInstrumentRefs(
  raw: OperationNodeSpec['recommendedInstrument'],
  fallbackWorkspaceId: string,
): RecommendedInstrumentRef[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const obj = entry as unknown as {
        value?: unknown;
        _id?: unknown;
        id?: unknown;
        workspaceId?: unknown;
        orgId?: unknown;
      };
      const value =
        (typeof obj.value === 'string' && obj.value.trim()) ||
        (typeof obj._id === 'string' && obj._id.trim()) ||
        (typeof obj.id === 'string' && obj.id.trim()) ||
        '';
      if (!value) return null;

      const workspaceId =
        (typeof obj.workspaceId === 'string' && obj.workspaceId.trim()) ||
        (typeof obj.orgId === 'string' && obj.orgId.trim()) ||
        fallbackWorkspaceId;

      return { value, workspaceId };
    })
    .filter((entry): entry is RecommendedInstrumentRef => Boolean(entry));
}

export function resolveCatalogEntryForEdit(
  operation: OperationNodeSpec,
  catalog: OperationTypeDefinition[],
): OperationTypeDefinition | undefined {
  if (operation.operationTypeId) {
    const byId = catalog.find((c) => c.id === operation.operationTypeId);
    if (byId) return byId;
  }

  const archetype = operation.archetype;
  const display = normalize(operation.displayName);

  const sameArchetype = catalog.filter((c) => c.operationType === archetype);
  const sameName = sameArchetype.filter((c) => normalize(c.operationName) === display);

  return pickLatest(sameName.length > 0 ? sameName : sameArchetype);
}

/**
 * Builds an AddStepFormState pre-populated from an existing operation.
 * Used when the StepModal opens in edit mode.
 */
export function buildEditFormState(
  operation: OperationNodeSpec,
  catalog: OperationTypeDefinition[],
): AddStepFormState {
  const catalogEntry = resolveCatalogEntryForEdit(operation, catalog);
  const workspaceId = catalogEntry?.workspaceId ?? '';

  const durationTotal = operation.duration?.value ?? 0;
  const hours = Math.floor(durationTotal / 3600);
  const minutes = Math.floor((durationTotal % 3600) / 60);
  const seconds = durationTotal % 60;

  return {
    ...INITIAL_FORM_STATE,
    selectedOperationTypeId: catalogEntry?.id ?? operation.operationTypeId ?? null,
    parameterValues: operation.parameters ?? {},
    yieldValue: operation.yield?.amount ?? null,
    yieldIsPercent: operation.yield?.isPercent ?? true,
    durationHours: hours,
    durationMinutes: minutes,
    durationSeconds: seconds,
    linkToPreviousStep: operation.duration?.linkToPreviousStep ?? false,
    linkedStepId: operation.duration?.prevStep ?? null,
    notes: operation.notes ?? operation.comments ?? '',
    recommendedInstrument: toRecommendedInstrumentRefs(operation.recommendedInstrument, workspaceId),
    approvers: operation.approvers ?? { approverType: null, users: [] },
    notifications: operation.notifications ?? { notificationType: null, users: [] },
    checkpoints: operation.checkPoints ?? [],
  };
}
